import { RocketChatRESTService } from '../services/RocketChatRESTService';
import { useChatPanelStore } from '../store/chatPanel.store';
import { useWorkplaceStore } from '@/host/stores';
import { useDisplayPrefsStore } from '../store/displayPrefs.store';

/**
 * Opens (creating if needed) a 1:1 Rocket.Chat DM with the given username and
 * makes it the active room. Reuses an already-loaded DM instead of calling
 * dm.create again — RC's own self-DM idempotency isn't reliable enough to
 * depend on, and calling it for a room we already have (including chatting
 * with yourself) can mint a second, duplicate room for the same conversation.
 *
 * Shared by every "start a chat with this person" entry point (People search,
 * Directory, Members panel "open DM", Platform Admin's user-detail chat
 * button) so they resolve/create the room the same way instead of each
 * reimplementing (and subtly diverging on) this dance.
 *
 * `displayName` is an optional fallback for the room's `fname` when the
 * caller already knows the person's display name (e.g. from a people-search
 * result) and RC's own dm.create response happens not to include one.
 */
export async function openDirectMessage(username: string, displayName?: string): Promise<boolean> {
  if (!username) return false;

  const store = useChatPanelStore.getState();
  const existing = Object.values(store.rooms).find((r) => r.t === 'd' && r.name === username);
  if (existing) {
    store.setActiveRoom(existing._id);
    return true;
  }

  try {
    const { rid, room } = await RocketChatRESTService.createDM(username);
    if (!rid) return false;
    useChatPanelStore.getState().upsertRoom({
      _id: rid,
      t: 'd',
      name: room?.name || username,
      fname: room?.fname || displayName || username,
      unread: 0,
      usernames: room?.usernames,
      _updatedAt: room?._updatedAt,
    });
    useChatPanelStore.getState().setActiveRoom(rid);
    return true;
  } catch {
    return false;
  }
}

/**
 * Ensures the Beta Chat (Rocket.Chat) drawer panel is open and showing the
 * default chat view. Idempotent — safe to call even if it's already open on
 * a different view. Unlike AppToolBar's chat-icon click handler, this never
 * toggles the drawer closed; it's for "go to chat" navigation from outside
 * the drawer (e.g. Platform Admin), not a repeatable icon-button toggle.
 */
export function ensureChatDrawerOpen(): void {
  const { drawerOpen, drawerView, setDrawerOpen, setDrawerView, setDrawerWidth } =
    useWorkplaceStore.getState();
  if (drawerOpen && drawerView?.appId === 'rocketchat') return;
  setDrawerOpen(true);
  setDrawerWidth(useDisplayPrefsStore.getState().panelWidth);
  setDrawerView({ appId: 'rocketchat', viewName: 'default', title: 'Chat' });
}
